import {
  Card,
  CardContent,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  ScrollArea,
} from "@atlas/core";
import { usePermission, useSessionData, useTranslation } from "@atlas/sdk";
import { CatalogTypeSection } from "@modules";
import { useHubCurrentCart, useHubPackage } from "@query";
import { defaultHubCartPackageId, HUB_PERM } from "@shared";
import { Store } from "lucide-react";
import { useMemo } from "react";
import type { IHubManagerCatalogSection } from "../useHubManagerCatalogSections";

interface ICatalogExplorerProps {
  sections: IHubManagerCatalogSection[];
  sectionsLoading: boolean;
  selectedTypeIds: string[];
}

export function CatalogExplorer({
  sections,
  sectionsLoading,
}: ICatalogExplorerProps) {
  const { t } = useTranslation();
  const { isPermission: hasCatalogBuy } = usePermission([HUB_PERM.CATALOG_BUY]);
  const { userId } = useSessionData();
  const uid = (userId ?? "").trim();
  const { data: userHubCart } = useHubCurrentCart(uid, {
    enabled: uid.length > 0 && hasCatalogBuy,
  });
  const hubCartPackageId = useMemo(
    () => defaultHubCartPackageId(userHubCart),
    [userHubCart],
  );
  const { data: cartPackage } = useHubPackage(hubCartPackageId, {
    enabled: hasCatalogBuy && hubCartPackageId > 0,
  });

  const showInitialLoading = sectionsLoading && sections.length === 0;

  if (showInitialLoading) {
    return (
      <Card
        variant="content"
        className="min-h-0 min-w-0 flex-1 border-0 shadow-none"
        isLoading>
        <CardContent />
      </Card>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-row gap-4 ">
      <ScrollArea
        className="min-h-0 min-w-0 flex-1"
        viewportClassName="flex flex-col gap-8 overscroll-y-contain pb-2 [-webkit-overflow-scrolling:touch]">
        {sections.length === 0 ? (
          <Empty className="mx-auto min-h-0 max-w-3xl flex-1">
            <EmptyMedia>
              <Store className="h-6 w-6 text-muted-foreground" />
            </EmptyMedia>
            <EmptyHeader>
              <EmptyTitle>
                {t("hub.catalog_manager.explorer_empty", {
                  defaultValue: "Explorer is empty.",
                })}
              </EmptyTitle>
            </EmptyHeader>
            <EmptyDescription>
              {t("hub.catalog_manager.explorer_empty_description", {
                defaultValue: "Explore the catalogs to add to your package.",
              })}
            </EmptyDescription>
          </Empty>
        ) : (
          sections.map((section, idx) => (
            <CatalogTypeSection
              key={`${section.catalogType.typeId ?? section.catalogType.name ?? "type"}-${idx}`}
              typeDescription={section.catalogType.description}
              typeLabel={section.catalogType.label}
              hubManagerTab="explorer"
              cartPackage={cartPackage}
              catalogs={section.catalogs}
              totalCount={section.totalCount}
              isLoading={section.isLoading}
              isRefetching={section.isRefetching}
              previewLimit={section.previewLimit}
              onViewAll={section.onViewAll}
            />
          ))
        )}
      </ScrollArea>
    </div>
  );
}
